import React from 'react';
import { CheckCircle, Users, Clock, Shield, Star, Award, Car } from 'lucide-react';

export default function CorporateCarLanding() {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* About Section */}
            <section className="pt-14 pb-8 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-6">
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-800 mb-1">
                            PVR CABS <span className="text-emerald-600">Corporate Solutions</span>
                        </h1>
                        <p className="text-sm md:text-sm lg:text-base font-medium text-slate-600 max-w-4xl mx-auto leading-relaxed">
                            PVR CABS offers a comprehensive corporate car rental service tailored to the needs of businesses in Telangana and Hyderabad. We provide high-end fleet solutions with customized services for various corporate requirements.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                            <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                                <Car className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">High-End Fleet</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600">Premium vehicles suitable for transporting groups of delegates or individual company associates with comfort and style.</p>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                            <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                                <Users className="w-8 h-8 text-slate-700" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Tailored Services</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600">Customized corporate car rental services catering to specific business needs and employee transportation requirements.</p>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                            <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                                <Award className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Corporate Partnerships</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600">Established tie-ups with top companies providing regular pick-up and drop-off services for employees and associates.</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                        <div className="grid lg:grid-cols-2 gap-6 items-center">
                            <div>
                                <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-slate-800 mb-4">Why Businesses Choose Us</h2>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-2">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                                        <p className="text-sm md:text-sm lg:text-base text-slate-600"><strong>Convenience:</strong> Save time and resources by outsourcing transport logistics to our expert team.</p>
                                    </div>
                                    <div className="flex items-start space-x-2">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                                        <p className="text-sm md:text-sm lg:text-base text-slate-600"><strong>Long-term Options:</strong> Extended rental services allowing businesses to book vehicles according to their requirements.</p>
                                    </div>
                                    <div className="flex items-start space-x-2">
                                        <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                                        <p className="text-sm md:text-sm lg:text-base text-slate-600"><strong>Vehicle Variety:</strong> Different vehicles to accommodate employees of various positions comfortably.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="relative">
                                <img
                                    src="/WhyChooseUs.jpg"
                                    alt="Corporate Transportation"
                                    className="rounded-xl shadow-lg w-full h-full md:h-80 object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="pt-14 pb-8 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-slate-800 mb-3">
                            Why Choose <span className="text-emerald-600">PVR CABS</span>
                        </h2>
                        <p className="text-sm md:text-base lg:text-lg font-medium text-slate-600 max-w-3xl mx-auto">
                            We deliver exceptional corporate transportation solutions that enhance your business operations and employee satisfaction.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="group bg-gray-50 hover:bg-white p-6 rounded-2xl transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-100">
                            <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-all">
                                <Star className="w-7 h-7 text-emerald-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">High-Quality Fleet</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600 leading-relaxed">
                                Our premium vehicle collection includes luxury sedans, SUVs, and executive cars, all maintained to the highest standards for your corporate needs.
                            </p>
                        </div>

                        <div className="group bg-gray-50 hover:bg-white p-6 rounded-2xl transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-100">
                            <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-800 transition-all">
                                <Users className="w-7 h-7 text-slate-700 group-hover:text-white" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Customized Solutions</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600 leading-relaxed">
                                Tailored transportation services designed specifically for your business requirements, from individual executive travel to group transportation.
                            </p>
                        </div>

                        <div className="group bg-gray-50 hover:bg-white p-6 rounded-2xl transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-100">
                            <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-all">
                                <Clock className="w-7 h-7 text-emerald-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Time & Cost Efficiency</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600 leading-relaxed">
                                Streamline your transportation logistics while reducing operational costs. Focus on your core business while we handle the mobility.
                            </p>
                        </div>

                        <div className="group bg-gray-50 hover:bg-white p-6 rounded-2xl transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-100">
                            <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-800 transition-all">
                                <Shield className="w-7 h-7 text-slate-700 group-hover:text-white" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Professional Drivers</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600 leading-relaxed">
                                Experienced, licensed, and courteous drivers who understand corporate etiquette and ensure safe, comfortable journeys.
                            </p>
                        </div>

                        <div className="group bg-gray-50 hover:bg-white p-6 rounded-2xl transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-100">
                            <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-all">
                                <CheckCircle className="w-7 h-7 text-emerald-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Flexibility</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600 leading-relaxed">
                                Adaptable booking options with short-term and long-term rental packages to meet your dynamic business requirements.
                            </p>
                        </div>

                        <div className="group bg-gray-50 hover:bg-white p-6 rounded-2xl transition-all duration-300 hover:shadow-xl border border-transparent hover:border-emerald-100">
                            <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-800 transition-all">
                                <Award className="w-7 h-7 text-slate-700 group-hover:text-white" />
                            </div>
                            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-slate-800 mb-3">Long-Term Partnerships</h3>
                            <p className="text-sm md:text-sm lg:text-base text-slate-600 leading-relaxed">
                                Established partnerships with top companies in Telangana and Hyderabad, providing reliable pick-up and drop-off services.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="pt-8 md:pt-14 pb-8 px-6 bg-gradient-to-r from-gray-100 to-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                        <div className="relative">
                            <img
                                src="/TransformTransport.jpg"
                                alt="Professional Corporate Transportation"
                                className="rounded-2xl shadow-2xl w-full h-72 object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent rounded-2xl"></div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-slate-800 mb-4">
                                    Ready to Transform Your
                                    <span className="text-emerald-600"> Corporate Transport?</span>
                                </h2>
                                <p className="text-sm md:text-base lg:text-lg font-medium text-slate-600 leading-relaxed mb-6">
                                    Join the leading companies in Telangana and Hyderabad who trust PVR CABS for their corporate transportation needs. Experience cost-effective, convenient, and reliable solutions that enhance productivity and employee satisfaction.
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <div className="text-xl md:text-2xl font-bold text-emerald-600">500+</div>
                                    <div className="text-xs md:text-sm text-slate-600">Happy Clients</div>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <div className="text-xl md:text-2xl font-bold text-slate-800">24/7</div>
                                    <div className="text-xs md:text-sm text-slate-600">Support</div>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <div className="text-xl md:text-2xl font-bold text-emerald-600">100%</div>
                                    <div className="text-xs md:text-sm text-slate-600">Satisfaction</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}